import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';

const API = process.env.REACT_APP_BACKEND_URL;
const COLORS = { primary: '#2b3380', whatsapp: '#25D366', success: '#4caf50', warning: '#ff9800', error: '#f44336' };

function WhatsAppAutomation({ user, onLogout }) {
  const [stats, setStats] = useState(null);
  const [autoReplies, setAutoReplies] = useState([]);
  const [scheduledMessages, setScheduledMessages] = useState([]);
  const [quickReplies, setQuickReplies] = useState([]);
  const [triggers, setTriggers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [logs, setLogs] = useState([]);
  const [activeTab, setActiveTab] = useState('auto-replies');
  const [showCreateReply, setShowCreateReply] = useState(false);
  const [showScheduleMessage, setShowScheduleMessage] = useState(false);

  const [replyForm, setReplyForm] = useState({
    name: '',
    trigger_type: 'keyword',
    trigger_value: '',
    reply_message: '',
    reply_type: 'text',
    template_name: '',
    delay_seconds: 0,
    priority: 1,
    business_hours_only: false,
    max_replies_per_day: 0,
    is_active: true
  });

  const [scheduleForm, setScheduleForm] = useState({
    recipient_phone: '',
    message: '',
    message_type: 'text',
    template_name: '',
    scheduled_time: '',
    repeat: '',
    is_active: true
  });

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const [statsRes, repliesRes, scheduledRes, quickRes, triggersRes, templatesRes, logsRes] = await Promise.all([
        axios.get(`${API}/api/whatsapp/automation/stats`, config),
        axios.get(`${API}/api/whatsapp/automation/auto-replies`, config),
        axios.get(`${API}/api/whatsapp/automation/scheduled-messages`, config),
        axios.get(`${API}/api/whatsapp/automation/quick-replies`, config),
        axios.get(`${API}/api/whatsapp/automation/triggered-messages`, config),
        axios.get(`${API}/api/whatsapp/templates`, config),
        axios.get(`${API}/api/whatsapp/automation/logs/auto-replies?limit=20`, config)
      ]);

      setStats(statsRes.data.stats);
      setAutoReplies(repliesRes.data.rules || []);
      setScheduledMessages(scheduledRes.data.messages || []);
      setQuickReplies(quickRes.data.quick_replies || []);
      setTriggers(triggersRes.data.triggers || []);
      setTemplates(templatesRes.data.data || []);
      setLogs(logsRes.data.logs || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleCreateAutoReply = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/whatsapp/automation/auto-replies`, replyForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('✅ Auto-reply rule created!');
      setShowCreateReply(false);
      setReplyForm({
        name: '',
        trigger_type: 'keyword',
        trigger_value: '',
        reply_message: '',
        reply_type: 'text',
        template_name: '',
        delay_seconds: 0,
        priority: 1,
        business_hours_only: false,
        max_replies_per_day: 0,
        is_active: true
      });
      fetchAllData();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleScheduleMessage = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/whatsapp/automation/scheduled-messages`, scheduleForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('✅ Message scheduled successfully!');
      setShowScheduleMessage(false);
      setScheduleForm({
        recipient_phone: '',
        message: '',
        message_type: 'text',
        template_name: '',
        scheduled_time: '',
        repeat: '',
        is_active: true
      });
      fetchAllData();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteAutoReply = async (ruleId) => {
    if (window.confirm('Delete this auto-reply rule?')) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`${API}/api/whatsapp/automation/auto-replies/${ruleId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('✅ Rule deleted!');
        fetchAllData();
      } catch (error) {
        alert('Error deleting rule');
      }
    }
  };

  const handleCancelScheduled = async (msgId) => {
    if (window.confirm('Cancel this scheduled message?')) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`${API}/api/whatsapp/automation/scheduled-messages/${msgId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('✅ Message cancelled!');
        fetchAllData();
      } catch (error) {
        alert('Error cancelling message');
      }
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('✅ Copied to clipboard!');
  };

  const tabs = [
    { id: 'auto-replies', label: '🤖 Auto-Replies', icon: '🤖' },
    { id: 'scheduled', label: '⏰ Scheduled Messages', icon: '⏰' },
    { id: 'quick-replies', label: '⚡ Quick Replies', icon: '⚡' },
    { id: 'triggers', label: '🎯 Event Triggers', icon: '🎯' },
    { id: 'logs', label: '📊 Logs', icon: '📊' }
  ];

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold mb-2" style={{ color: COLORS.primary }}>
            🤖 WhatsApp Automation
          </h1>
          <p className="text-gray-600">Automate responses, schedule messages, and streamline communication</p>
        </div>

        {/* Stats Dashboard */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-600 mb-1">Active Auto-Replies</div>
              <div className="text-3xl font-bold" style={{ color: COLORS.primary }}>{stats.active_auto_replies}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-600 mb-1">Event Triggers</div>
              <div className="text-3xl font-bold" style={{ color: COLORS.success }}>{stats.active_triggers}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-600 mb-1">Scheduled Messages</div>
              <div className="text-3xl font-bold" style={{ color: COLORS.warning }}>{stats.pending_scheduled}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-600 mb-1">Replies (30d)</div>
              <div className="text-3xl font-bold text-blue-600">{stats.auto_reply_executions_30d}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-600 mb-1">Total Automations</div>
              <div className="text-3xl font-bold" style={{ color: COLORS.whatsapp }}>{stats.total_automations}</div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-gray-300 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-3 font-medium transition whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-b-4 text-blue-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
              style={activeTab === tab.id ? { borderColor: COLORS.primary } : {}}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-lg shadow p-6">
          {/* Auto-Replies Tab */}
          {activeTab === 'auto-replies' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold" style={{ color: COLORS.primary }}>🤖 Auto-Reply Rules</h2>
                <button
                  onClick={() => setShowCreateReply(true)}
                  className="px-6 py-2 text-white rounded-lg font-medium hover:opacity-90"
                  style={{ backgroundColor: COLORS.whatsapp }}
                >
                  ➕ Create Rule
                </button>
              </div>

              {showCreateReply && (
                <div className="mb-6 p-6 bg-gray-50 rounded-lg border border-gray-300">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold">Create Auto-Reply Rule</h3>
                    <button onClick={() => setShowCreateReply(false)} className="text-gray-500 hover:text-gray-700">
                      ✕
                    </button>
                  </div>
                  <form onSubmit={handleCreateAutoReply} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Rule Name *</label>
                        <input
                          type="text"
                          value={replyForm.name}
                          onChange={(e) => setReplyForm({...replyForm, name: e.target.value})}
                          required
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                          placeholder="Welcome Message"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Trigger Type *</label>
                        <select
                          value={replyForm.trigger_type}
                          onChange={(e) => setReplyForm({...replyForm, trigger_type: e.target.value})}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                        >
                          <option value="keyword">Keyword</option>
                          <option value="pattern">Pattern (Regex)</option>
                          <option value="greeting">Greeting (Auto-detect)</option>
                          <option value="question">Question (Auto-detect)</option>
                          <option value="any">Any Message</option>
                        </select>
                      </div>
                    </div>

                    {(replyForm.trigger_type === 'keyword' || replyForm.trigger_type === 'pattern') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {replyForm.trigger_type === 'keyword' ? 'Keywords (comma-separated)' : 'Pattern (Regex)'}
                        </label>
                        <input
                          type="text"
                          value={replyForm.trigger_value}
                          onChange={(e) => setReplyForm({...replyForm, trigger_value: e.target.value})}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                          placeholder={replyForm.trigger_type === 'keyword' ? 'hello, hi, hey' : '^order.*status$'}
                        />
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Reply Message *</label>
                      <textarea
                        value={replyForm.reply_message}
                        onChange={(e) => setReplyForm({...replyForm, reply_message: e.target.value})}
                        required
                        rows="3"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                        placeholder="Hello! Thank you for contacting us..."
                      />
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Priority (1-10)</label>
                        <input
                          type="number"
                          value={replyForm.priority}
                          onChange={(e) => setReplyForm({...replyForm, priority: parseInt(e.target.value)})}
                          min="1"
                          max="10"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                        />
                        <p className="text-xs text-gray-500 mt-1">1 = highest priority</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Delay (seconds)</label>
                        <input
                          type="number"
                          value={replyForm.delay_seconds}
                          onChange={(e) => setReplyForm({...replyForm, delay_seconds: parseInt(e.target.value)})}
                          min="0"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                        />
                        <p className="text-xs text-gray-500 mt-1">0 = instant</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Daily Limit</label>
                        <input
                          type="number"
                          value={replyForm.max_replies_per_day}
                          onChange={(e) => setReplyForm({...replyForm, max_replies_per_day: parseInt(e.target.value)})}
                          min="0"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                        />
                        <p className="text-xs text-gray-500 mt-1">0 = unlimited</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-6">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={replyForm.business_hours_only}
                          onChange={(e) => setReplyForm({...replyForm, business_hours_only: e.target.checked})}
                          className="w-5 h-5"
                        />
                        <span className="text-sm font-medium text-gray-700">Business Hours Only (9 AM - 6 PM)</span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={replyForm.is_active}
                          onChange={(e) => setReplyForm({...replyForm, is_active: e.target.checked})}
                          className="w-5 h-5"
                        />
                        <span className="text-sm font-medium text-gray-700">Active</span>
                      </label>
                    </div>

                    <button
                      type="submit"
                      className="w-full px-6 py-3 text-white rounded-lg font-semibold hover:opacity-90"
                      style={{ backgroundColor: COLORS.success }}
                    >
                      ✨ Create Auto-Reply Rule
                    </button>
                  </form>
                </div>
              )}

              {/* Auto-Reply List */}
              <div className="space-y-3">
                {autoReplies.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No auto-reply rules yet. Create your first one!</p>
                ) : (
                  autoReplies.map((rule) => (
                    <div key={rule.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h4 className="font-semibold text-lg">{rule.name}</h4>
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              rule.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'
                            }`}>
                              {rule.is_active ? '✓ Active' : '✗ Inactive'}
                            </span>
                            {rule.business_hours_only && (
                              <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700">
                                ⏰ Business Hours
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-600 space-y-1">
                            <p><strong>Trigger:</strong> {rule.trigger_type} {rule.trigger_value && `= "${rule.trigger_value}"`}</p>
                            <p><strong>Reply:</strong> {rule.reply_message.substring(0, 100)}{rule.reply_message.length > 100 ? '...' : ''}</p>
                            <p>
                              <strong>Settings:</strong> Priority: {rule.priority || 1} | 
                              Delay: {rule.delay_seconds || 0}s | 
                              Daily Limit: {rule.max_replies_per_day || 'Unlimited'}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleDeleteAutoReply(rule.id)}
                          className="px-4 py-2 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Scheduled Messages Tab */}
          {activeTab === 'scheduled' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold" style={{ color: COLORS.primary }}>⏰ Scheduled Messages</h2>
                <button
                  onClick={() => setShowScheduleMessage(true)}
                  className="px-6 py-2 text-white rounded-lg font-medium hover:opacity-90"
                  style={{ backgroundColor: COLORS.whatsapp }}
                >
                  ➕ Schedule Message
                </button>
              </div>

              {showScheduleMessage && (
                <div className="mb-6 p-6 bg-gray-50 rounded-lg border border-gray-300">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold">Schedule a Message</h3>
                    <button onClick={() => setShowScheduleMessage(false)} className="text-gray-500 hover:text-gray-700">
                      ✕
                    </button>
                  </div>
                  <form onSubmit={handleScheduleMessage} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Recipient Phone *</label>
                        <input
                          type="tel"
                          value={scheduleForm.recipient_phone}
                          onChange={(e) => setScheduleForm({...scheduleForm, recipient_phone: e.target.value})}
                          required
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                          placeholder="+919599900999"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Scheduled Time *</label>
                        <input
                          type="datetime-local"
                          value={scheduleForm.scheduled_time}
                          onChange={(e) => setScheduleForm({...scheduleForm, scheduled_time: e.target.value + ':00.000Z'})}
                          required
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
                      <textarea
                        value={scheduleForm.message}
                        onChange={(e) => setScheduleForm({...scheduleForm, message: e.target.value})}
                        required
                        rows="4"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                        placeholder="Your scheduled message..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Repeat (Optional)</label>
                      <select
                        value={scheduleForm.repeat}
                        onChange={(e) => setScheduleForm({...scheduleForm, repeat: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                      >
                        <option value="">One-time only</option>
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                      </select>
                    </div>

                    <button
                      type="submit"
                      className="w-full px-6 py-3 text-white rounded-lg font-semibold hover:opacity-90"
                      style={{ backgroundColor: COLORS.success }}
                    >
                      ⏰ Schedule Message
                    </button>
                  </form>
                </div>
              )}

              {/* Scheduled Messages List */}
              <div className="space-y-3">
                {scheduledMessages.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No scheduled messages yet.</p>
                ) : (
                  scheduledMessages.map((msg) => (
                    <div key={msg.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-xl">📱</span>
                            <span className="font-medium">{msg.recipient_phone}</span>
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              msg.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                              msg.status === 'sent' ? 'bg-green-100 text-green-700' :
                              'bg-gray-200 text-gray-600'
                            }`}>
                              {msg.status}
                            </span>
                            {msg.repeat && (
                              <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-700">
                                🔄 {msg.repeat}
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-600 mb-2">{msg.message}</div>
                          <div className="text-xs text-gray-500">
                            Scheduled: {new Date(msg.scheduled_time).toLocaleString()}
                          </div>
                        </div>
                        {msg.status === 'pending' && (
                          <button
                            onClick={() => handleCancelScheduled(msg.id)}
                            className="px-4 py-2 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200"
                          >
                            Cancel
                          </button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Quick Replies Tab */}
          {activeTab === 'quick-replies' && (
            <div>
              <h2 className="text-2xl font-bold mb-6" style={{ color: COLORS.primary }}>⚡ Quick Reply Templates</h2>
              <p className="text-gray-600 mb-6">Pre-built templates for common responses. Click to copy!</p>
              
              <div className="space-y-6">
                {quickReplies.map((category, idx) => (
                  <div key={idx}>
                    <h3 className="text-xl font-semibold mb-3">{category.category}</h3>
                    <div className="space-y-2">
                      {category.replies.map((reply, ridx) => (
                        <div
                          key={ridx}
                          className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition cursor-pointer"
                          onClick={() => copyToClipboard(reply.text)}
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <code className="px-2 py-1 bg-gray-200 text-sm rounded font-mono">{reply.shortcut}</code>
                                <span className="text-sm text-gray-500">Click to copy</span>
                              </div>
                              <p className="text-gray-700">{reply.text}</p>
                            </div>
                            <span className="text-2xl">📋</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Event Triggers Tab */}
          {activeTab === 'triggers' && (
            <div>
              <h2 className="text-2xl font-bold mb-6" style={{ color: COLORS.primary }}>🎯 Event Triggers</h2>
              <p className="text-gray-600 mb-6">Auto-send WhatsApp messages when ERP events occur</p>
              
              {triggers.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <p className="text-gray-500 mb-4">No event triggers configured yet.</p>
                  <p className="text-sm text-gray-400">Event triggers are created by admins to send notifications for events like:</p>
                  <ul className="text-sm text-gray-400 mt-2">
                    <li>• Leave approvals</li>
                    <li>• Order confirmations</li>
                    <li>• Payslip availability</li>
                    <li>• Payment receipts</li>
                  </ul>
                </div>
              ) : (
                <div className="space-y-3">
                  {triggers.map((trigger) => (
                    <div key={trigger.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-semibold capitalize">{trigger.event_type.replace('_', ' ')}</h4>
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          trigger.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'
                        }`}>
                          {trigger.is_active ? '✓ Active' : '✗ Inactive'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">Template: {trigger.template_name}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Logs Tab */}
          {activeTab === 'logs' && (
            <div>
              <h2 className="text-2xl font-bold mb-6" style={{ color: COLORS.primary }}>📊 Execution Logs</h2>
              
              {logs.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No logs yet</p>
              ) : (
                <div className="space-y-2">
                  {logs.map((log, idx) => (
                    <div key={idx} className="border border-gray-200 rounded-lg p-4 text-sm">
                      <div className="flex justify-between items-start mb-2">
                        <div className="font-medium">{log.rule_name}</div>
                        <div className="text-xs text-gray-500">{new Date(log.created_at).toLocaleString()}</div>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-gray-600">
                        <div>
                          <span className="font-medium">From:</span> {log.from_number}
                        </div>
                        <div>
                          <span className="font-medium">Delay:</span> {log.delay_seconds || 0}s
                        </div>
                      </div>
                      <div className="mt-2 text-gray-600">
                        <span className="font-medium">Incoming:</span> {log.incoming_message}
                      </div>
                      <div className="mt-1 text-gray-600">
                        <span className="font-medium">Reply:</span> {log.reply_sent}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default WhatsAppAutomation;